import { useRef, useState } from "react";

export function useVoiceRecording(onTranscriptUpdate) {
    const [isRecording, setIsRecording] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    const wsRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const deepgramReadyRef = useRef(false);
    const closingRef = useRef(false);
    const recordingTimerRef = useRef(null);

    const cleanup = (resetState = false) => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }
        mediaRecorderRef.current = null;

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
        }

        if (wsRef.current) {
            try {
                if (
                    wsRef.current.readyState === WebSocket.OPEN ||
                    wsRef.current.readyState === WebSocket.CONNECTING
                ) {
                    wsRef.current.close();
                }
            } catch (e) {
                console.error("Error closing websocket:", e);
            }
            wsRef.current = null;
        }

        deepgramReadyRef.current = false;
        closingRef.current = false;

        if (resetState) {
            setIsRecording(false);
            setIsConnecting(false);
            setRecordingTime(0);
        }
    };

    const setupMediaRecorder = (stream, ws) => {
        const options = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            ? { mimeType: "audio/webm;codecs=opus" }
            : undefined;

        const mediaRecorder = options
            ? new MediaRecorder(stream, options)
            : new MediaRecorder(stream);

        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
            if (
                deepgramReadyRef.current &&
                !closingRef.current &&
                event.data.size > 0 &&
                ws.readyState === WebSocket.OPEN
            ) {
                ws.send(event.data);
            }
        };
    };

    const startTimer = (onStop) => {
        recordingTimerRef.current = setInterval(() => {
            setRecordingTime((prev) => {
                if (prev >= 60) {
                    onStop();
                    return prev;
                }
                return prev + 1;
            });
        }, 1000);
    };

    const startRecording = async (onStop) => {
        try {
            setIsConnecting(true);
            closingRef.current = false;
            deepgramReadyRef.current = false;
            setRecordingTime(0);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            const ws = new WebSocket(process.env.REACT_APP_WS_VOICE_URL || "ws://localhost:8080/ws/voice");
            wsRef.current = ws;

            ws.onopen = () => {
                setupMediaRecorder(stream, ws);
            };

            ws.onmessage = (event) => {
                if (event.data === "DG_READY") {
                    deepgramReadyRef.current = true;
                    setIsConnecting(false);

                    if (
                        mediaRecorderRef.current &&
                        mediaRecorderRef.current.state === "inactive"
                    ) {
                        mediaRecorderRef.current.start(100);
                        setIsRecording(true);
                        startTimer(onStop);
                    }
                    return;
                }

                onTranscriptUpdate(String(event.data || "").trim());
            };

            ws.onerror = (error) => {
                console.error("WS ERROR", error);
                console.error("WebSocket error:", error);
                setIsConnecting(false);
            };

            ws.onclose = (event) => {
                wsRef.current = null;
                deepgramReadyRef.current = false;
                setIsRecording(false);
                setIsConnecting(false);
            };

            return stream;
        } catch (error) {
            console.error("Error starting recording:", error);
            setIsConnecting(false);
            alert("Unable to access microphone: " + error.message);
            return null;
        }
    };

    const finalizeAndClose = async () => {
        closingRef.current = true;

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
        }

        setIsRecording(false);

        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send("FINALIZE");
            await new Promise((resolve) => setTimeout(resolve, 1200));

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send("CLOSE_STREAM");
            }

            await new Promise((resolve) => setTimeout(resolve, 300));

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.close();
            }
        }
    };

    return {
        isRecording,
        isConnecting,
        recordingTime,
        startRecording,
        finalizeAndClose,
        cleanup,
    };
}
